/**
 * Real `AuthAPI` implementation backed by the HTTP client (ADR-0018
 * §3). Three methods: `signIn`, `currentUser`, `signOut`. The
 * `verify` flow is intentionally absent — per ADR-0018 §6.2 verify is
 * a top-level browser navigation (`window.location.assign`), not a
 * JS-driven fetch; the dashboard's `<Verify />` view drives the
 * navigation directly and never calls into the auth bag.
 *
 * Response shape mapping:
 *   - sign-in:    204 → resolve void; 429 → AuthRateLimitError;
 *                 other → AuthError (via HttpClient).
 *   - me:         200 + `{ user }` → resolve user; 401 →
 *                 AuthUnauthorizedError; other → AuthError.
 *   - sign-out:   204 → resolve void; 401 (already signed out) is
 *                 swallowed so the caller's "user signed out" flow
 *                 still completes; other → AuthError.
 *
 * The `currentUser` unwraps `{ user }` — per ADR-0018 §3.1 the server
 * envelope wraps the User payload; the dashboard's hydrated state is
 * the bare User.
 */

import type { AuthAPI } from '@sill/types/api';
import { AuthError, AuthUnauthorizedError } from '@sill/types/api';
import type { User } from '@sill/types';

import type { HttpClient } from './http-client.js';
import { ApiError } from './http-client.js';

/** Agency Portal v2 §4.5 — slug grammar mirror of the API-side regex. */
const AGENCY_REFERRAL_SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,30}[a-z0-9]$/;

/**
 * Resolve the agency referral slug attribution for a sign-in POST.
 * Reads two sources in priority order:
 *
 *   1. `?ref=ag_<slug>` on the current URL (visitor just clicked
 *      an agency's referral link);
 *   2. `document.cookie` → `sill_ref=<slug>` (set by the sill.so
 *      Pages Function on `.sill.so`; survives nav).
 *
 * Returns `null` on malformed input / unavailable globals / EU-only
 * link mode (no cookie). A bad value NEVER blocks sign-in.
 */
function resolveAgencyReferralSlug(): string | null {
  try {
    const g = globalThis as unknown as {
      location?: { search?: string };
      document?: { cookie?: string };
    };
    const search = g.location?.search ?? '';
    const m = /[?&]ref=ag_([a-z0-9][a-z0-9_-]{1,30}[a-z0-9])(?:&|$)/.exec(search);
    if (m !== null) {
      const slug = m[1];
      if (typeof slug === 'string' && AGENCY_REFERRAL_SLUG_RE.test(slug)) return slug;
    }
    const cookie = g.document?.cookie ?? '';
    const cm = /(?:^|;\s*)sill_ref=([a-z0-9][a-z0-9_-]{1,30}[a-z0-9])(?:;|$)/.exec(cookie);
    if (cm !== null) {
      const slug = cm[1];
      if (typeof slug === 'string' && AGENCY_REFERRAL_SLUG_RE.test(slug)) return slug;
    }
  } catch {
    // SSR / private mode / weird globals — fail silent.
  }
  return null;
}

export interface RealAuthDeps {
  readonly http: HttpClient;
}

interface MeResponse {
  user: User;
  // ADR-0216 — non-null when the resolving session is a support
  // session (staff impersonation). We fold this onto the returned
  // User so the AuthProvider hydrates one shape.
  support_session?: {
    readonly staff_id: string;
    readonly expires_at: string;
    readonly account_name: string;
    readonly allow_changes?: boolean;
  } | null;
  // ADR-0237 — non-null when the resolving session is an agency acting
  // session. Folded onto the returned User so the dashboard reads a
  // single shape from `useAuth()` for both support + acting contexts.
  acting_context?: NonNullable<User['acting_context']> | null;
}

export function createRealAuthApi(deps: RealAuthDeps): AuthAPI {
  return {
    async signIn(email: string, prefill_domain?: string): Promise<void> {
      // Normalize prefill_domain like email (qa #570 minor 7) — the
      // marketing caller of the same wire field trims + lowercases;
      // both callers of one field must agree. Whitespace-only → omit.
      const domain = prefill_domain?.trim().toLowerCase() ?? '';
      // Agency Portal v2 §4.5 — carry agency attribution. Priority:
      //   1. `?ref=ag_<slug>` on the current URL (fresh click);
      //   2. the shared-domain cookie `sill_ref=<slug>` on `.sill.so`;
      // A malformed value drops silently (no attribution ever blocks
      // sign-in). Server dedupes first-link-wins + drops suspended.
      const slug = resolveAgencyReferralSlug();
      const attribution = slug !== null ? { agency_referral_slug: slug } : undefined;
      const body = {
        email: email.trim(),
        ...(domain.length > 0 ? { prefill_domain: domain } : {}),
        ...(attribution !== undefined ? { attribution } : {}),
      };
      // One automatic retry after 2 s on a network error so a transient
      // connectivity blip doesn't surface as a visible error on first try.
      // Rate-limit errors (429) are NOT retried — they carry a Retry-After
      // the UI surfaces to the user.
      try {
        await deps.http.request<undefined>({ method: 'POST', path: '/v1/auth/sign-in', body });
      } catch (err) {
        if (
          err instanceof ApiError
          && (err.kind === 'network_error' || err.kind === 'network_timeout')
        ) {
          await new Promise((r) => setTimeout(r, 2000));
          await deps.http.request<undefined>({ method: 'POST', path: '/v1/auth/sign-in', body });
          return;
        }
        throw err;
      }
    },

    async currentUser(): Promise<User> {
      // ADR-0018 §7 — the bootstrap auth check sets
      // throw_on_unauthorized so it can handle the 401 inline (route
      // to login) rather than triggering the provider-level callback
      // before the provider has mounted.
      const res = await deps.http.request<MeResponse>({
        method: 'GET',
        path: '/v1/auth/me',
        throw_on_unauthorized: true,
      });
      if (res === undefined) {
        throw new AuthError('me_returned_no_body');
      }
      // ADR-0146 §3.3 — `notification_preferences` is additive on the
      // wire (the server SELECT was extended in the same slice). A
      // deploy where the API is older than the dashboard surfaces the
      // field as `undefined`; default to `{}` so consumers can treat
      // an absent preference as "all defaults" (every owner/admin/
      // reviewer implicitly opted in to the escalation digest).
      const user: User = {
        ...res.user,
        notification_preferences: res.user.notification_preferences ?? {},
        // ADR-0216 — fold the support_session field (or its absence)
        // onto the returned user. The persistent banner reads
        // `user.support_session != null` to decide whether to render.
        support_session: res.support_session ?? null,
        // ADR-0237 — fold the acting_context field (or its absence)
        // onto the returned user. The ActingBanner + AccountSwitcher
        // read `user.acting_context` as the single source of truth
        // for whether the session is in an agency acting state.
        acting_context: res.acting_context ?? null,
      };
      return user;
    },

    async signOut(): Promise<void> {
      try {
        await deps.http.request<undefined>({
          method: 'POST',
          path: '/v1/auth/sign-out',
        });
      } catch (err) {
        // A 401 on sign-out means the session was already invalidated
        // server-side (cookie expired, manual DB revoke, etc.). From
        // the dashboard's perspective this is the same end-state as
        // a successful sign-out — drop the local user state and
        // route to login. Re-throw any other AuthError so the caller
        // can surface a soft-retry copy.
        if (err instanceof AuthUnauthorizedError) {
          return;
        }
        throw err;
      }
    },
  };
}
